import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { toast } from "sonner";
import { UserPlus } from "lucide-react";

const COUNTRY_CODES = ["nl","be","fr","de","uk","at","it","es","pt","lu","ch","se","dk","pl"];

export default function RegisterPage() {
  const [searchParams] = useSearchParams();
  const isAffiliate = searchParams.get("type") === "affiliate";
  const [form, setForm] = useState({
    name: "", email: "", password: "", password_confirm: "", company_name: "",
    vat_number: "", country: "nl", language: "nl", ref: searchParams.get("ref") || "", captcha_answer: ""
  });
  const [loading, setLoading] = useState(false);
  const [captcha, setCaptcha] = useState(null);
  const { register } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/auth/captcha").then((r) => setCaptcha(r.data)).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password !== form.password_confirm) { toast.error(t("toast.passwords_mismatch")); return; }
    if (form.password.length < 8) { toast.error(t("toast.password_min")); return; }
    setLoading(true);
    try {
      if (captcha?.token) api.defaults.headers.common["X-Captcha-Token"] = captcha.token;
      await register(form);
      delete api.defaults.headers.common["X-Captcha-Token"];
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || t("toast.register_failed"));
      api.get("/auth/captcha").then((r) => setCaptcha(r.data)).catch(() => {});
    } finally { setLoading(false); }
  };

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 py-12" data-testid="register-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-block" data-testid="register-logo"><img src="/logo.jpg" alt="Smartpriced" className="h-12 mx-auto" /></Link>
        </div>
        <Card className="shadow-xl shadow-slate-200/50 border-slate-100">
          <CardHeader className="text-center pb-2">
            <CardTitle className="text-xl">{isAffiliate ? t("affiliate.register_title") : t("nav.register")}</CardTitle>
            <CardDescription>{isAffiliate ? t("affiliate.register_desc") : t("register.trial_desc")}</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div><Label>{t("form.name")} *</Label><Input value={form.name} onChange={update("name")} placeholder={t("form.name_full")} required className="mt-1.5" data-testid="register-name-input" /></div>
              <div><Label>{t("form.email")} *</Label><Input type="email" value={form.email} onChange={update("email")} placeholder="je@email.com" required className="mt-1.5" data-testid="register-email-input" /></div>
              <div><Label>{t("form.password")} * ({t("form.password_min")})</Label><Input type="password" value={form.password} onChange={update("password")} placeholder={t("form.password_min")} required className="mt-1.5" data-testid="register-password-input" /></div>
              <div><Label>{t("form.password_confirm")} *</Label><Input type="password" value={form.password_confirm} onChange={update("password_confirm")} placeholder={t("form.password_confirm")} required className="mt-1.5" data-testid="register-password-confirm-input" />
                {form.password_confirm && form.password !== form.password_confirm && <p className="text-xs text-red-500 mt-1">{t("toast.passwords_mismatch")}</p>}
              </div>
              <div><Label>{t("form.company")}</Label><Input value={form.company_name} onChange={update("company_name")} placeholder={t("form.company")} className="mt-1.5" data-testid="register-company-input" /></div>
              <div><Label>{t("form.country")}</Label>
                <select value={form.country} onChange={update("country")} className="mt-1.5 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid="register-country-select">
                  {COUNTRY_CODES.map((c) => <option key={c} value={c}>{t(`shops.country_${c}`)}</option>)}
                </select>
                <p className="text-xs text-muted-foreground mt-1">{t("form.country_hint")}</p>
              </div>
              <div><Label>{t("form.vat")}</Label><Input value={form.vat_number} onChange={update("vat_number")} placeholder="BE0123456789" className="mt-1.5" data-testid="register-vat-input" /></div>
              {captcha && (
                <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                  <Label className="text-sm">{t("form.captcha")}: <strong className="text-[#1A3F6E]">{captcha.question}</strong></Label>
                  <Input value={form.captcha_answer} onChange={update("captcha_answer")} placeholder={t("form.answer")} required className="mt-1.5 w-24" data-testid="register-captcha-input" />
                </div>
              )}
              <Button type="submit" className="w-full bg-[#2CA8AC] hover:bg-[#248F92] h-11 mt-2" disabled={loading || (form.password_confirm && form.password !== form.password_confirm)} data-testid="register-submit-button">
                {loading ? t("admin.loading") : <><UserPlus className="w-4 h-4 mr-2" />{isAffiliate ? t("affiliate.register_title") : t("hero.cta")}</>}
              </Button>
            </form>
            <p className="text-center text-sm text-slate-500 mt-6">
              {t("register.has_account")} <Link to="/login" className="text-[#2CA8AC] hover:text-[#248F92] font-medium" data-testid="register-login-link">{t("nav.login")}</Link>
            </p>
          </CardContent>
        </Card>
        <div className="mt-6 text-center">
          <Link to="/" className="text-sm text-[#2CA8AC] hover:text-[#248F92] font-medium" data-testid="register-home-link">{t("register.back_home")}</Link>
        </div>
      </div>
    </div>
  );
}
